/**
 * The boot-time initial sync.
 *
 * `DatabaseWrapper` holds two different things at once: a replication engine
 * that keeps this device's local PouchDB in step with the remote CouchDB, and a
 * document-access API (get / save / query) that the rest of the app calls. This
 * file holds one piece of the first one — the code that runs once at boot and
 * decides whether this device has to wait for a download before the app may be
 * used, then keeps retrying that download while the device is online.
 *
 * Like `database-wrapper-sync-start.ts` and `database-wrapper-pull-catchup.ts`,
 * it deliberately knows nothing about `DatabaseWrapper`. Everything it touches
 * arrives through the `InitialSyncContext` object below, which is why the module
 * can be exercised in a unit test with a small fake instead of a whole database
 * wrapper — the class itself imports `pouchdb-browser`, which reads `self` at
 * module scope and cannot even be LOADED outside a browser, so before this move
 * nothing here could be run by a test at all.
 *
 * Nothing here changed behaviour when it moved out of `database-wrapper.ts`.
 * The rules below were already true and are restated because they are easy to
 * break with an edit that looks harmless:
 *
 *  - **The three early returns before any sync wait MUST release the
 *    calendar-window gate** (#4249): `remoteOnly`, offline, and routed to a temporary database.
 *    Boot waits on that gate. A `remoteOnly` database never syncs, and an
 *    offline cold boot never calls `startSync` at all, so in those cases nothing
 *    downstream would ever release it — boot would sit on the splash screen for
 *    the whole `CALENDAR_SYNC_BOOT_WAIT_MS` on EVERY offline launch. The later
 *    returns do not release it themselves: a database routed after the probe
 *    gets it from `startSync`'s own `temporaryDb` return, and a database
 *    recovery takeover happens after a sync has started. See
 *    `design-docs/sync-integrity.md`, which pins the count of release sites.
 *  - **A database routed to a temporary one never syncs, so nothing here waits
 *    for it** (df-4880745a2773). Impersonation points the wrapper at a local
 *    snapshot, and `startSync` then returns without starting anything, so the
 *    deferreds below would never settle. Routing is set by the caller just
 *    AFTER this function starts, so it is checked after the first-probe wait
 *    and again after every failed wait. Once routed, this function stops
 *    without restarting, reporting or writing either success marker.
 *  - **The first-probe wait comes BEFORE any connection-state read** (#3988
 *    F11). The connection state boots as `Offline` until the first reachability
 *    ping resolves, so reading it earlier makes a genuinely-online cold boot
 *    skip its initial sync and fall back on later recovery events. The
 *    `remoteOnly` return is the one thing allowed to come first, because it
 *    never reads the state and must not pay the gate's latency.
 *  - **Inside the retry loop, the "a concurrent sync already succeeded" check
 *    comes BEFORE the offline check.** A success followed by going offline is
 *    still a success, not a failure to report.
 *  - **A shutdown mid-retry is not a sync failure.** Sign-out and account
 *    switch null `localDb` underneath this loop; reporting that would emit a
 *    spurious exhaustion signal.
 *  - **On exhaustion, only the OFFLINE case closes the pass-through gate.**
 *    While online the gate stays open so reads keep reaching the remote. See
 *    `design-docs/pass-through-db.md`.
 *  - **The two success markers are separate and mean different things** (#3991
 *    F24). `settled` says this device has seen whatever the server had, even if
 *    that was nothing; `recentlySynced` is deliberately NOT written when the
 *    remote turned out to be empty. Merging them makes the offline-boot gate
 *    mistake a brand-new signup for an account that was simply never downloaded
 *    here.
 */

import { DateTime } from 'luxon';
import {
  INITIAL_SYNC_EARLY_REPORT_ATTEMPT,
  INITIAL_SYNC_MAX_RETRY_ATTEMPTS,
  nextInitialSyncRetryDelayMs,
} from './database-wrapper-sync-boot-policy';

/** What one bounded wait on a sync deferred answered. */
export interface SyncWaitOutcome {
  /** the deferred's own value; anything falsy means the sync did not succeed */
  succeeded: boolean | undefined;
  /** true when the wait hit its own bound rather than the sync settling */
  timedOut: boolean;
}

/**
 * Everything the initial sync needs from the database wrapper around it.
 *
 * Grouped on purpose, and a new member goes in the group it belongs to: state
 * this module only reads, values that live on the wrapper, and things it asks
 * the wrapper to do. A member here is not an invitation to add more — each one
 * is a piece of the document-access side that the sync engine has not been
 * separated from yet. See deferred finding `df-3c79746add23`.
 */
export interface InitialSyncContext {
  // ---- state this module only reads ----

  /** set for a database that exists only on the server; it never syncs locally */
  readonly remoteOnly: boolean;
  /**
   * set once the wrapper reads and writes a temporary database (impersonation);
   * no sync ever runs for it, so the waits below would never settle
   */
  readonly routedToTemporaryDb: boolean;
  readonly localDb: any;
  /** the name used in log lines and telemetry, e.g. `user` */
  readonly internalDatabaseIdentifier: string;
  readonly initialSyncCompleted: boolean | undefined;
  /** set when the initial sync finished because the remote held nothing at all */
  readonly initialSyncSkippedEmpty: boolean;
  /** the singleton document ids, reported in the failure context */
  readonly singletonDocIds: string[];
  /**
   * The connection state, in the three shapes this module consumes it. The
   * booleans are not each other's negation by contract — the wrapper derives
   * both from the same enum, and reading them separately keeps this module from
   * having to import that enum, which lives in a module that reaches the
   * Capacitor and environment layer and cannot load outside a browser.
   */
  readonly connectionStateIsOnline: boolean;
  readonly connectionStateIsOffline: boolean;
  /** the connection state's own value, published verbatim in the Sentry context */
  readonly connectionState: string;

  // ---- values that live on the wrapper ----

  /** the CouchDB address, reported in the failure context */
  readonly couchServerUrl: string;
  /** localStorage key holding when this database last synced */
  readonly recentlySyncedStorageKey: string;
  /** localStorage key holding when this database's initial sync last SETTLED */
  readonly initialSyncSettledStorageKey: string;

  // ---- database recovery, and the reporting route, kept apart on purpose ----

  /**
   * The wrapper's own `DatabaseRecoveryService`. Used here for one thing only: deciding
   * whether a failed IndexedDB health probe means recovery is already under way,
   * in which case this function returns rather than reporting.
   */
  readonly databaseRecovery: {
    /** true when the error was taken over by database recovery */
    interceptDatabaseError(err: unknown): boolean;
  };
  /**
   * `dbProvider.logging` — the route the original code took for every Sentry
   * report in this function, and the only thing this context reports through.
   * Database recovery is asked separately, above.
   */
  readonly reportingLogging: {
    trackSilentProblem(name: string, context: { [key: string]: any }): void;
    trackSilentInfo(name: string, context: { [key: string]: any }): void;
  };

  /**
   * The retry text shown on the boot screen. Called through the context rather
   * than imported, because the translation service reaches the environment layer
   * and cannot load outside a browser. Both are called at the moment the message
   * is shown, so a locale change is picked up the same way it was before.
   */
  readonly translations: {
    retrying(): string;
    stillRetrying(): string;
  };

  // ---- things it asks the wrapper to do ----

  isUserDb(): boolean;
  /** true for an account that has just been created on this device */
  isUserNewAsync(): Promise<boolean>;
  /**
   * Waits (bounded) for the first reachability probe to resolve, so the
   * connection state read straight afterwards is a real answer rather than the
   * `Offline` placeholder it boots as.
   */
  awaitFirstConnectionProbeAsync(): Promise<void>;
  /**
   * Releases whatever boot is holding on the calendar window. Safe to call when
   * nothing is waiting; the underlying deferred resolves once and ignores the rest.
   */
  resolveCalendarWindowSyncGate(): void;
  /** waits out the singleton documents' own sync, bounded by `timeoutMs` */
  awaitInitialSingletonSyncAsync(timeoutMs: number): Promise<SyncWaitOutcome>;
  /** waits out the whole initial sync, bounded by `timeoutMs` */
  awaitInitialSyncAsync(timeoutMs: number): Promise<SyncWaitOutcome>;
  /**
   * Arms a fresh SINGLETON sync attempt: new singleton deferred, cleared last
   * error, cancelled handler, and — only when there is a remote to talk to — a
   * fresh full-sync deferred too, because the restarted sync drives both.
   */
  restartSyncForSingletonRetry(): void;
  /**
   * Arms a fresh FULL sync attempt: new full-sync deferred, cleared last error,
   * cancelled handler, and a restarted sync when there is a remote to talk to.
   * Deliberately NOT the same as the singleton version above — that one resets
   * the full-sync deferred only when a remote exists, this one always does.
   */
  restartSyncForInitialSyncRetry(): void;
  /** shows (or with null, clears) the retry text on the boot loading screen */
  publishRetryMessage(retryMessage: string | null): void;
  /**
   * Waits `ms` before the next retry. It arrives through the context rather than
   * being imported so a test can run this code without waiting minutes of real
   * time — and, more usefully, can assert the delays that were ASKED for, which
   * is the backoff schedule itself. The wrapper passes the same `sleep` this
   * function called directly before the move.
   */
  sleepAsync(ms: number): Promise<void>;
  /** cancels the live sync handler so a later resume can start a fresh one */
  cancelSync(): void;
  setInitialSyncCompleted(value: boolean): void;
  /** the last sync error, already flattened to text for a log line */
  stringifiedLastSyncError(): string | null;
}

/**
 * Runs the boot-time initial sync for one database and returns when this device
 * either has the data, has given up, or never needed to wait in the first place.
 *
 * Fire-and-forget by every caller: boot does not await this, it waits on the
 * deferred promises this function resolves. See `design-docs/pass-through-db.md`.
 *
 * The work happens in three stages, called in order below: the singleton wait,
 * the full-sync wait, and recording what came of it. The ordering rules in the
 * module header are each restated where they apply.
 */
export async function assureInitialSyncInternalAsync(context: InitialSyncContext): Promise<void> {
  // remoteOnly DBs never read connectionState, so return before the probe
  // wait below — they must not pay the gate latency for a value they ignore.
  if (context.remoteOnly) {
    // no sync will run, so release anything waiting on the calendar window —
    // boot must not hold the splash for a sync that is never going to happen
    // (header rule: the early returns before any sync wait MUST release the calendar-window gate)
    context.resolveCalendarWindowSyncGate();
    return;
  }
  // F11 (#3988): the connection state boots as Offline until the first ping
  // resolves. Wait (bounded) for that first probe so a genuinely-online cold
  // boot does not read the placeholder below, skip its initial sync, and end
  // up depending on later recovery events. The bound exceeds the probe's own
  // timeout so a slow-but-online ping is awaited to completion, not cut short.
  // (header rule: the first-probe wait comes BEFORE any connection-state read)
  await context.awaitFirstConnectionProbeAsync();
  if (context.connectionStateIsOffline) {
    // an offline cold boot never calls startSync, so nothing downstream would
    // ever resolve the calendar gate — boot would sit on the splash for the
    // whole CALENDAR_SYNC_BOOT_WAIT_MS on EVERY offline launch. Release it.
    // (header rule: the early returns before any sync wait MUST release the calendar-window gate)
    context.resolveCalendarWindowSyncGate();
    return;
  }
  // if impersonation routed this database to a snapshot, no sync will run, so
  // there is nothing to wait for and nothing to report (df-4880745a2773).
  // checked after the probe wait because the caller routes it just after this
  // function starts. (header rule: the early returns before any sync wait MUST release the gate)
  if (context.routedToTemporaryDb) {
    console.log(`Db:${context.internalDatabaseIdentifier}: routed to a temporary database - skipping the initial sync wait.`);
    context.resolveCalendarWindowSyncGate();
    return;
  }

  const recentlySynced_utc = localStorage[context.recentlySyncedStorageKey];
  const recentlySynced = recentlySynced_utc ? DateTime.fromISO(recentlySynced_utc) : null;
  const forceSync = !context.isUserDb() && (await context.isUserNewAsync());

  // captured before the singleton wait on purpose: the "recently synced" check
  // below has always measured from here
  const now = DateTime.utc();

  // if user db, wait for singleton docs to sync first (with retries)
  if (context.isUserDb()) {
    const singletonOutcome = await awaitSingletonSyncWithRetriesAsync(context);
    // if database recovery took the failure over, it owns what happens next —
    // the full sync below must not run against a dead IndexedDB
    if (singletonOutcome === SingletonStageOutcome.TakenOverByDatabaseRecovery) {
      return;
    }
  }

  // if routed during the singleton stage, the full sync will never run either.
  // the gate is not released here: startSync's own temporaryDb return does that
  // (header rule: a routed database never syncs, so nothing here waits for it)
  if (context.routedToTemporaryDb) {
    return;
  }

  // if sync is needed (not recently synced)
  if (forceSync || !recentlySynced || now.diff(recentlySynced, 'minutes').minutes > 60) {
    const { succeeded, retryAttempt } = await awaitFullSyncWithRetriesAsync(context);
    // if routed mid-retry, the failure is the missing sync, not a real one
    if (!succeeded && context.routedToTemporaryDb) {
      return;
    }
    await recordInitialSyncOutcomeAsync(context, succeeded, retryAttempt);
  } else {
    console.log(`Db:${context.internalDatabaseIdentifier}: Recently synced - not waiting for initial sync.`);
    context.setInitialSyncCompleted(true);
  }
}

/** How the singleton stage ended, as far as the rest of the initial sync cares. */
enum SingletonStageOutcome {
  /** carry on with the full sync, whether or not the singletons synced */
  Continue = 'Continue',
  /** the IndexedDB probe failed and database recovery took it over; stop here */
  TakenOverByDatabaseRecovery = 'TakenOverByDatabaseRecovery',
}

/**
 * Stage 1, user database only: waits for the singleton documents to sync, retries
 * twice with backoff while online, and reports a failure. A singleton failure is
 * not fatal — the full sync that follows retries it — so the only thing this
 * stage hands back is whether database recovery took over.
 */
async function awaitSingletonSyncWithRetriesAsync(context: InitialSyncContext): Promise<SingletonStageOutcome> {
  const singletonSyncStart = Date.now();
  let { succeeded: singletonSucceeded, timedOut } = await context.awaitInitialSingletonSyncAsync(30_000);

  // retry singleton sync with backoff if it failed and we're still online
  const singletonRetryDelays = [3000, 6000];
  let singletonRetry = 0;
  while (!singletonSucceeded && singletonRetry < singletonRetryDelays.length
         && context.connectionStateIsOnline && !context.routedToTemporaryDb) {
    const delay = singletonRetryDelays[singletonRetry];
    console.log(`Db:${context.internalDatabaseIdentifier}: Initial singleton sync failed, retrying in ${delay}ms (attempt ${singletonRetry + 1}/${singletonRetryDelays.length})...`);
    await context.sleepAsync(delay);

    // if went offline or was routed to a temporary database during the wait, stop retrying
    if (!context.connectionStateIsOnline || context.routedToTemporaryDb) {
      break;
    }

    context.restartSyncForSingletonRetry();
    ({ succeeded: singletonSucceeded, timedOut } = await context.awaitInitialSingletonSyncAsync(30_000));
    singletonRetry++;
  }

  // if routed to a temporary database, no singleton sync was ever going to run,
  // so its absence is not a failure (header rule: a routed database never syncs)
  if (!singletonSucceeded && context.routedToTemporaryDb) {
    return SingletonStageOutcome.Continue;
  }

  if (!singletonSucceeded) {
    // if IDB is dead, recovery is needed instead of reporting
    if (await isTakenOverByDatabaseRecoveryAsync(context)) {
      return SingletonStageOutcome.TakenOverByDatabaseRecovery;
    }

    const singletonDocs = context.singletonDocIds;
    const errorContext = {
      timedOut,
      elapsedMs: Date.now() - singletonSyncStart,
      connectionState: context.connectionState,
      documentHidden: document.hidden,
      singletonDocs,
      couchUrl: context.couchServerUrl,
      lastSyncError: context.stringifiedLastSyncError(),
    };
    // stringify to avoid Sentry breadcrumb [Object] truncation — log as info since main sync retry follows
    console.log(`Db:${context.internalDatabaseIdentifier}: Initial singleton sync failed (main sync will retry). Error context:`,
      JSON.stringify(errorContext, null, 2));
    // if document is hidden (iOS backgrounding), this is expected — don't report to Sentry
    if (!document.hidden) {
      context.reportingLogging.trackSilentProblem(
        `Db:${context.internalDatabaseIdentifier}: Initial singleton sync failed`,
        errorContext,
      );
    }
  }
  return SingletonStageOutcome.Continue;
}

/**
 * Stage 2: waits for the whole initial sync and keeps retrying with capped backoff
 * while online. Returns whether it succeeded and how many retries it took, because
 * stage 3 names the retry count in its failure report and log line.
 */
async function awaitFullSyncWithRetriesAsync(
  context: InitialSyncContext,
): Promise<{ succeeded: boolean | undefined; retryAttempt: number }> {
  let { succeeded } = await context.awaitInitialSyncAsync(60_000);

  // retry logic (#3698): keep retrying with capped backoff while online.
  // the loop exits when sync succeeds, the device goes offline, the
  // wrapper is shut down (localDb gone), or the attempt cap is reached.
  // PouchDB checkpoints replication progress, so each restart resumes
  // from the last sequence — retries do not redo completed work.
  let retryAttempt = 0;
  let earlyReportEmitted = false;
  while (!succeeded && retryAttempt < INITIAL_SYNC_MAX_RETRY_ATTEMPTS && context.localDb != null
         && context.connectionStateIsOnline && !context.routedToTemporaryDb) {
    const delay = nextInitialSyncRetryDelayMs(retryAttempt);
    const retryMessage = retryAttempt === 0 ? context.translations.retrying() : context.translations.stillRetrying();
    console.log(`Db:${context.internalDatabaseIdentifier}: Initial sync failed, retrying in ${delay}ms (attempt ${retryAttempt + 1}/${INITIAL_SYNC_MAX_RETRY_ATTEMPTS})...`);

    // report-then-keep-trying (#3698): emit the exhaustion signal once,
    // at (or after, if the app was backgrounded at exactly that tick) the
    // legacy two-retry budget so prod telemetry fires early — this state
    // was previously invisible because sessions rarely survived to the
    // end of a long retry run. the loop keeps retrying after reporting.
    if (!earlyReportEmitted && retryAttempt >= INITIAL_SYNC_EARLY_REPORT_ATTEMPT && !document.hidden) {
      earlyReportEmitted = true;
      context.reportingLogging.trackSilentProblem(
        `Db:${context.internalDatabaseIdentifier}: Initial sync still failing after ${INITIAL_SYNC_EARLY_REPORT_ATTEMPT} retries (online — continuing to retry)`,
        {
          database: context.internalDatabaseIdentifier,
          lastSyncError: context.stringifiedLastSyncError(),
          retriesAttempted: retryAttempt,
        },
      );
    }

    // show retry message on the boot loading screen
    context.publishRetryMessage(retryMessage);
    await context.sleepAsync(delay);

    // if a concurrently-kicked sync (splash watchdog / app resume) completed
    // during the wait, take the success — resetting the flag below would
    // regress a completed sync and cancel it for nothing. checked BEFORE
    // the offline break: a success followed by going offline is still a
    // success, not a failure to report
    // (header rule: the concurrent-success check comes BEFORE the offline check)
    if (context.initialSyncCompleted === true) {
      succeeded = true;
      break;
    }

    // if went offline, the wrapper was shut down, or it was routed to a temporary
    // database during the wait, stop retrying
    if (!context.connectionStateIsOnline || context.localDb == null || context.routedToTemporaryDb) {
      break;
    }

    // reset deferred promise and reattempt sync
    context.restartSyncForInitialSyncRetry();

    ({ succeeded } = await context.awaitInitialSyncAsync(60_000));
    retryAttempt++;
  }

  // clear retry message
  context.publishRetryMessage(null);

  return { succeeded, retryAttempt };
}

/**
 * Stage 3: acts on what stage 2 found — reports and adjusts the pass-through gate
 * on failure, or writes the two success markers on success.
 */
async function recordInitialSyncOutcomeAsync(
  context: InitialSyncContext,
  succeeded: boolean | undefined,
  retryAttempt: number,
): Promise<void> {
  // if the wrapper was shut down mid-retry (sign-out, account switch),
  // this is an intentional teardown, not a sync failure — skip all
  // failure reporting (it would emit a spurious "exhausted retries"
  // signal, and offline it would even show a user-visible error toast)
  // (header rule: a shutdown mid-retry is not a sync failure)
  if (!succeeded && context.localDb == null) {
    console.log(`Db:${context.internalDatabaseIdentifier}: Initial sync retry loop stopped — database was shut down`);
    return;
  }

  if (!succeeded) {
    // clear dead/timed-out sync handler so ensureSyncActive() can restart on app resume
    context.cancelSync();

    // hybrid failure mode: only close the pass-through gate when offline.
    // while online, leave initialSyncCompleted === undefined so reads keep
    // hitting remote (graft populates local opportunistically) and
    // ensureSyncActive() on the next resume / reconnect retries initial
    // sync. going Offline → Online resets the gate via ensureSyncActive
    // for any stranded DBs that closed below.
    // (header rule: on exhaustion, only the OFFLINE case closes the pass-through gate)
    if (context.connectionStateIsOnline) {
      console.log(`Db:${context.internalDatabaseIdentifier}: Initial sync exhausted retries while Online — leaving gate open for pass-through, will retry on next ensureSyncActive`);
    } else {
      // fall back to localDb so getReadDb() stops returning unreachable remoteDb
      context.setInitialSyncCompleted(false);
    }

    // if IDB is dead, recovery is needed instead of reporting
    if (await isTakenOverByDatabaseRecoveryAsync(context)) {
      return;
    }

    const errorContext = {
      database: context.internalDatabaseIdentifier,
      lastSyncError: context.stringifiedLastSyncError(),
      isOnline: context.connectionStateIsOnline,
      retriesAttempted: retryAttempt,
      documentHidden: document.hidden,
    };
    console.error(`Db:${context.internalDatabaseIdentifier}: Initial sync failed after ${retryAttempt} retries. Error context:`, errorContext);

    // three reporting paths:
    //  - if backgrounded (likely mobile Safari/Chrome network suspension): silent
    //  - if foreground + online (hybrid recovery mode — gate stays open, pass-
    //    through keeps the app usable): silent, since the user is not blocked
    //  - if foreground + offline (gate closed, reads fall back to local): info
    //    only, no toast (#6349). being offline is expected: the offline toast,
    //    yellow line and Offline pill already tell the user in their language,
    //    and a never-synced device is held on the splash with a Retry button.
    //    going back Online reopens the gate and retries via ensureSyncActive
    if (document.hidden) {
      context.reportingLogging.trackSilentProblem(
        `Db:${context.internalDatabaseIdentifier}: Initial sync failed (background)`,
        errorContext,
      );
    } else if (context.connectionStateIsOnline) {
      context.reportingLogging.trackSilentProblem(
        `Db:${context.internalDatabaseIdentifier}: Initial sync exhausted retries (online — pass-through active)`,
        errorContext,
      );
    } else {
      context.reportingLogging.trackSilentInfo(
        `Db:${context.internalDatabaseIdentifier}: Initial sync failed (offline)`,
        errorContext,
      );
    }
  } else {
    console.log(`Db:${context.internalDatabaseIdentifier}: Initial sync completed.`);
    // the sync finished, so this device has now seen whatever the server had for this
    // database — even if that was nothing. record that separately from recentlySynced,
    // because the offline-boot gate must not mistake a brand-new signup for an existing
    // account that has simply never been downloaded here (F24, #3991)
    // (header rule: the two success markers are separate and mean different things)
    localStorage[context.initialSyncSettledStorageKey] = DateTime.utc().toISO();
    // if sync was trivially completed (remote was empty), don't set recentlySynced —
    // a page reload (e.g. PPG redirect) would skip real sync and boot with empty singletons
    if (!context.initialSyncSkippedEmpty) {
      // fresh timestamp — the "recently synced" check's `now` was captured at
      // function entry, which with the long retry loop (#3698) can be many
      // minutes stale by this point
      localStorage[context.recentlySyncedStorageKey] = DateTime.utc().toISO();
    }
  }
}

/**
 * Probes IndexedDB after a sync failure. Returns true when the probe failed AND
 * database recovery took that error over — the caller then returns rather than
 * reporting, because recovery owns the failure now. A probe error recovery does
 * not claim returns false, and the caller reports as usual.
 */
async function isTakenOverByDatabaseRecoveryAsync(context: InitialSyncContext): Promise<boolean> {
  try {
    await context.localDb.info();
  } catch (probeErr) {
    if (context.databaseRecovery.interceptDatabaseError(probeErr)) {
      return true;
    }
  }
  return false;
}
